import { useAppStore } from "../stores/appStore";
import { useChatStore } from "../stores/chatStore";

const SYNC_DEBOUNCE_MS = 300;
const MIN_SYNC_INTERVAL_MS = 8000;
const BOOTSTRAP_GRACE_MS = 10000;

let syncInFlight: Promise<void> | null = null;
let lastSyncCompletedAt = 0;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let pageSessionConnectCount = 0;

export const markSocketConnected = (): boolean => {
  pageSessionConnectCount += 1;
  return pageSessionConnectCount === 1;
};

export const shouldSyncMissedMessages = (): boolean => {
  const {
    isAssessmentOpen,
    isPreassessmentOpen,
    deviceConflictModalOpen,
    refreshChat,
  } = useAppStore.getState();
  const { isBootstrapping, lastBootstrapAt } = useChatStore.getState();

  if (
    isAssessmentOpen ||
    isPreassessmentOpen ||
    deviceConflictModalOpen ||
    refreshChat ||
    isBootstrapping
  ) {
    return false;
  }

  if (
    lastBootstrapAt > 0 &&
    Date.now() - lastBootstrapAt < BOOTSTRAP_GRACE_MS
  ) {
    return false;
  }

  if (
    lastSyncCompletedAt > 0 &&
    Date.now() - lastSyncCompletedAt < MIN_SYNC_INTERVAL_MS
  ) {
    return false;
  }

  return true;
};

export const runMissedMessageSync = async (
  run: () => Promise<void>,
): Promise<boolean> => {
  if (!shouldSyncMissedMessages() || syncInFlight) {
    return false;
  }

  syncInFlight = run()
    .catch((error) => {
      console.error("Error syncing missed messages:", error);
    })
    .finally(() => {
      lastSyncCompletedAt = Date.now();
      syncInFlight = null;
    });

  await syncInFlight;
  return true;
};

export const scheduleMissedMessageSync = (
  run: () => Promise<void>,
): void => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void runMissedMessageSync(run);
  }, SYNC_DEBOUNCE_MS);
};

export const scheduleMissedMessageSyncAfterReconnect = (
  run: () => Promise<void>,
): void => {
  if (pageSessionConnectCount <= 1) {
    return;
  }
  scheduleMissedMessageSync(run);
};

export const scheduleMissedMessageSyncAfterTabVisible = (
  hiddenDurationMs: number,
  run: () => Promise<void>,
): void => {
  if (hiddenDurationMs < 2000) {
    return;
  }
  scheduleMissedMessageSync(run);
};
